import MediaThumbnail from "../../../components/common/MediaThumbnail";
import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ArrowLeft, Building2, CircleCheck, ChevronRight, Home, ImageOff, Layers, MapPin, Scan } from "lucide-react";
import "./ProjectDetails.css";

function ProjectPhoto({ src, alt, eager = false }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  if (!src || failed) return <div className="public-detail-photo-fallback" role="img" aria-label={alt}><ImageOff aria-hidden="true" /><span>Image unavailable</span></div>;
  return <MediaThumbnail thumbnailWidth={eager ? 1400 : 800} src={src} alt={alt} loading={eager ? "eager" : "lazy"} decoding="async" onError={() => setFailed(true)} />;
}

function ProjectDetails({ projectsList }) {
  const { state } = useLocation();
  const { id } = useParams();
  const project = state || projectsList?.find((item) => String(item.id) === id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  if (!project) return <div className="public-project-detail public-detail-empty">
    <Building2 aria-hidden="true" />
    <h1>Project details not found.</h1>
    <p>Open a project from the projects listing to view its details.</p>
    <Link className="public-detail-back" to="/projects"><ArrowLeft aria-hidden="true" />Back to Projects</Link>
  </div>;

  const name = project.projectCode || project.title || project.id;
  const status = /^(ongoing|in progress|in_progress)$/i.test(project.status) ? "Ongoing" : String(project.status || "").replace(/_/g, " ");
  const gallery = (project.images || []).filter((item) => typeof item === "string" ? item : item?.url || item?.imageUrl || item?.thumbnailUrl);
  const facts = [
    { label: "Area", icon: Scan, value: typeof project.area === "number" ? `${project.area.toLocaleString("en-IN")} sq ft` : project.area || "Not specified" },
    { label: "Foundation", icon: Building2, value: project.foundation || project.floorLabel || "Not specified" },
    { label: "Category", icon: Layers, value: project.category || "Not specified" },
    { label: "Status", icon: CircleCheck, value: status || "Not specified" },
  ];

  return <div className="public-project-detail">
    <nav className="public-detail-breadcrumb" aria-label="Breadcrumb">
      <Link to="/"><Home aria-hidden="true" />Home</Link><ChevronRight aria-hidden="true" />
      <Link to="/projects">Projects</Link><ChevronRight aria-hidden="true" />
      <span aria-current="page">{name}</span>
    </nav>

    <section className="public-detail-hero" aria-labelledby="public-detail-title">
      <div className="public-detail-cover">
        <ProjectPhoto src={project.imageUrl || gallery[0]} alt={`${name} exterior`} eager />
      </div>
      <div className="public-detail-summary">
        <div className="public-detail-toolbar">
          <Link className="public-detail-back" to="/projects"><ArrowLeft aria-hidden="true" />Back to Projects</Link>
        </div>
        <h1 id="public-detail-title">{name}</h1>
        <p className="public-detail-location"><MapPin aria-hidden="true" />{project.location || "Location not specified"}</p>
        <dl className="public-detail-facts">{facts.map(({ label, icon: Icon, value }) => <div key={label}>
          <dt><Icon aria-hidden="true" /><span>{label}</span></dt><dd>{value}</dd>
        </div>)}</dl>
      </div>
    </section>

    <section className="public-detail-gallery" aria-labelledby="public-detail-gallery-title">
      <div className="public-detail-section-heading">
        <h2 id="public-detail-gallery-title">Project Gallery</h2>
        <p>A glimpse of the project at different stages and perspectives.</p>
      </div>
      {gallery.length ? <div className="public-detail-gallery-grid">{gallery.map((src, index) => <div className="public-detail-gallery-photo" key={`${src}-${index}`}>
        <ProjectPhoto src={src} alt={`${name} — project view ${index + 1}`} />
      </div>)}</div> : <p className="public-detail-gallery-empty">Project photos will appear here when available.</p>}
    </section>
    {project.description && <section className="public-detail-description"><h2>About the Project</h2><p>{project.description}</p></section>}
  </div>;
}

export default ProjectDetails;
